import { surveyPhoto } from "../../utils/profilePhoto";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Coins, 
  LockKeyhole,
  Share2,
  Bookmark,
  UserRound,
} from "lucide-react";
import { toast } from "react-toastify";
import photolg from "../../assets/img/photo-1.jpg";
import star from "../../assets/img/star.svg";
import RedButton from "../RedButton";
import dummyProfile from "../../assets/img/dummy_profile.png";
import { saveSurvey } from "../../service/surveyForMe";

const formatDate = (dateStr) => {
  if (!dateStr) return "10 Jan 2026";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "10 Jan 2026";
    const day = d.getDate();
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  } catch (e) {
    return "10 Jan 2026";
  }
};

const SurveyViewCard = ({ item }) => {
    const navigate = useNavigate();
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isSaved, setIsSaved] = useState(Boolean(item.is_saved));
    const [saving, setSaving] = useState(false);
      
    useEffect(() => {
      const token = localStorage.getItem("token");
      if (token) {
        setIsLoggedIn(true);
      }
    }, []);

    const handleSave = async (e) => {
      e?.preventDefault();
      if (saving) return;
      try {
        setSaving(true);
        const response = await saveSurvey({ survey_id: item.id });
        if (response && response.data && response.data.success) {
          setIsSaved(true);
          toast.success(response.data.message || "Survey saved successfully!");
        } else {
          toast.error(response?.data?.message || "Failed to save survey.");
        }
      } catch (error) {
        console.error("Error saving survey:", error);
        toast.error(
          error.response?.data?.message || "An error occurred while saving the survey."
        );
      } finally {
        setSaving(false);
      }
    };

    const BASE_URL = process.env.REACT_APP_API_URL || "https://iptestapi.irbureau.com";
    const imageUrl = item.survey_image 
      ? (item.survey_image.startsWith("http") ? item.survey_image : `${BASE_URL}${item.survey_image}`)
      : (item.image || photolg);

    const title = item.survey_title || item.title || "How Do Remote Teams Choose Async Collaboration Tools?";
    const desc = item.survey_description || item.description || "Help map what drives adoption across startups and distributed product teams.";
    const reward = item.reward_per_participant || item.reward || "0";

    const authorName = item.author?.name || item.author_name || (item.first_name ? `${item.first_name} ${item.last_name || ""}`.trim() : "Aisha Mehta");
    const authorRole = item.author?.role || item.author_role || item.job_title || "Product Researcher";
    const avatarUrl = surveyPhoto(item);

    const formattedDate = formatDate(item.start_date || item.created_at || item.createdAt);

    const tags = [];
    if (Array.isArray(item.survey_tags) && item.survey_tags.length > 0) {
      tags.push(...item.survey_tags.map(t => t.startsWith("#") ? t : `#${t.trim()}`));
    } else {
      if (item.sector) tags.push(`#${item.sector.replace(/\s+/g, "")}`);
      if (item.industry) tags.push(`#${item.industry.replace(/\s+/g, "")}`);
      if (item.country) tags.push(`#${item.country.replace(/\s+/g, "")}`);
      if (tags.length === 0 && Array.isArray(item.tags)) {
        tags.push(...item.tags.map(t => t.startsWith("#") ? t : `#${t}`));
      }
    }
    const displayTags = tags.length > 0 ? tags : ["#Technology", "#SaaS", "#Workplace"];

    return (
        <>
        <div className="post-card mb-4">

            {/* Profile Header Row */}
            <div className="d-flex align-items-center mb-3">
              <img 
                src={avatarUrl} 
                alt={authorName} 
                className="post-user-img" 
                style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = dummyProfile;
                }}
              />

              <div className="ms-3">
                <h5 className="ft-15 fw-bold mb-1" style={{ color: "#2c2e33" }}>{authorName}</h5>

                <p className="ft-12 mb-0 text-muted">
                  {authorRole} <span>-</span> {formattedDate}
                </p>
              </div>
            </div>

            <div className={`position-relative mb-4 ${!isLoggedIn ? "sr-locked-banner" : ""}`}>
                <img 
                  src={imageUrl} 
                  alt={title} 
                  className="post-banner" 
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = photolg;
                  }}
                />

                <div className="sr-reward-badge">
                    <img src={star} alt="star" /> Reward: {reward} Coins
                </div>

                {!isLoggedIn && (
                <div className="sr-lock-icon">
                    <LockKeyhole size={56} strokeWidth={2.1} />
                </div>
                )}
            </div>

            <h2 className="post-title fw-bold mb-3 ft-20">
                {title}
            </h2>

            <p className="post-desc ft-12 mb-3">
                {desc}
            </p>

            <div className="d-flex flex-wrap gap-2 mb-4">
                {displayTags.map((tag) => (
                  <span className="tag-box" key={tag}>{tag}</span>
                ))}
            </div>

            <div className="col-12 mt-16">
                
                {isLoggedIn ? (

                    <div className="d-flex align-items-center justify-content-between">

                        <div className="d-flex gap-2 items-center">
                        
                        <button
                            type="button"
                            className="btn border-0 rounded-3 d-flex align-items-center justify-content-center"
                            style={{
                            width: "40px",
                            height: "40px",
                            background: "#FFF4F4",
                            }}
                        >
                            <Share2 size={18} color="#2F343B" />
                        </button>

                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            className="btn border-0 rounded-3 d-flex align-items-center justify-content-center"
                            style={{
                            width: "40px",
                            height: "40px",
                            background: isSaved ? "#FFEBEB" : "#FFF4F4",
                            cursor: "pointer",
                            }}
                            title={isSaved ? "Saved" : "Save Survey"}
                        >
                            <Bookmark size={18} color={isSaved ? "#C72430" : "#2F343B"} fill={isSaved ? "#C72430" : "none"} />
                        </button>

                        </div>

                        <RedButton
                            btntext="Start Survey"
                            name="startsurvey"
                            onClick={() => navigate(`/take-survey/${item.id}`, { state: { surveyMeta: item } })}
                        />

                    </div>

                    ) : (

                    <div className="d-flex align-items-center justify-content-between p-3 rounded-3"
                        style={{
                            background: "#FFF1F1",
                            border: "1px solid #F5D6D6",
                        }}
                        >
                        <div className="d-flex align-items-center gap-2">
                            <UserRound size={14} color="#6B7280" />

                            <span
                            className="fw-semibold"
                            style={{
                                color: "#6B7280",
                                fontSize: "13px",
                            }}
                            >
                            Sign Up To Unlock Full Details, Eligibility And Insights.
                            </span>
                        </div>

                        <RedButton
                            btntext="Unlock Full Details"
                            name="unlockFullDetails"
                            onClick={() => navigate("/")}
                        />

                    </div>
                        
                    )
                }    
                
            </div>
        </div>        
        </>
    );
};

export default SurveyViewCard;
